/**
 * JWT token utilities
 */

interface JWTPayload {
  exp: number; // Expiration time (Unix timestamp)
  sub: string; // Subject (user ID)
  [key: string]: unknown;
}

/**
 * Decode JWT token without verification
 * Note: This only decodes the payload, does not verify signature
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param token JWT token string
 * @param bufferSeconds Optional buffer time in seconds (default: 60)
 * @returns true if token is expired or will expire within buffer time
 */
export function isTokenExpired(token: string | null, bufferSeconds: number = 60): boolean {
  if (!token) return true;

  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;

  // Get current time in seconds
  const currentTime = Math.floor(Date.now() / 1000);

  // Check if token is expired or will expire within buffer time
  return payload.exp < currentTime + bufferSeconds;
}

/**
 * Get the expiration time of a JWT token
 */
export function getTokenExpiration(token: string | null): Date | null {
  if (!token) return null;

  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return null;

  return new Date(payload.exp * 1000);
}
